import React, { useRef, useState } from 'react';
import { MdOutlineCalendarMonth } from 'react-icons/md';
import Tag from 'ui/components/atoms/Tag';
import ShareButton from 'ui/components/atoms/ShareButton';
import { useSelector } from 'react-redux';
import dateFormatter from 'ui/helpers/utils/dateFormatter';
import triggerNativeMessage from '../../../helpers/utils/triggerNativeMessage';
import dynamic from 'next/dynamic';
import useIsNativeView from 'ui/hooks/useIsNativeView';
import { IRootState } from '../../../store';
import useImpressionProductKnowledgeDetail from '../../../hooks/emarsys/product-knowledge/useImpressionProductKnowledgeDetail';
import eventClickProductKnowledgeDetail from '../../../helpers/emarsys/product-knowledge/eventClickProductKnowledgeDetail';
import useImpressionNewsDetail from 'ui/hooks/emarsys/news/useImpressionNewsDetail';
import eventClickNewsDetail from 'ui/helpers/emarsys/news/eventClickNewsDetail';

const ModalShare = dynamic(() => import('ui/components/atoms/ModalShare'), {
  ssr: false,
});

const DetailArticleBodySection = ({
  article,
  additionalClassName = '',
  enableSharing = false,
  screenSizeData = {
    screenSize: {
      height: 673,
      mobile: false,
      width: 1920,
    },
  },
  showTag = true,
  emarsysData = {
    breadcrumbs: [],
  },
  feature = '',
}) => {
  const impressionRef = useRef(null);
  const [modalVisible, setModalVisible] = useState(false);
  const { screenSize } = enableSharing
    ? screenSizeData
    : useSelector((state: IRootState) => state.page);

  const isNativeView = useIsNativeView();

  // Trigger share system on mobile
  const handleShare = async () => {
    if (screenSize.mobile && navigator.share) {
      try {
        await navigator.share({
          title: article.titlePage,
          text: 'Check out this article!',
          url: typeof window !== 'undefined' ? window.location.href : '',
        });
      } catch (error) {
        console.error('Share error:', error);
        // open modal if failed open navigator.share() except cancelation
        if (!error.toString().includes('AbortError')) {
          setModalVisible(true);
        }
      }
    } else {
      setModalVisible(true);
    }
  };

  const handleShareButtonClicked = (message, titlePage) => {
    triggerNativeMessage(message, { titlePage });

    if (isNativeView) {
      setModalVisible(false);
    } else {
      setModalVisible(true);
    }
  };

  if (feature === 'PRODUCT_KNOWLEDGE_DETAIL') {
    useImpressionProductKnowledgeDetail(
      impressionRef,
      article?.titlePage,
      emarsysData.breadcrumbs,
      article?.publishedDate,
      'detail review produk'
    );
  }
  if (feature === 'NEWS_DETAIL') {
    useImpressionNewsDetail(impressionRef, article, emarsysData?.breadcrumbs, 'news detail');
  }

  return (
    <React.Fragment>
      <section className={`${(additionalClassName = '')} mx-4 lg:mx-0 lg:mt-5`} ref={impressionRef}>
        <div>
          {/* Tag */}
          {showTag && !enableSharing && article?.contentCategory?.name && (
            <Tag className="hidden rounded mb-2 lg:inline-block" severity={`warn`}>
              <span className="text-lg font-medium capitalize">
                {article?.contentCategory?.name.toLowerCase()}
              </span>
            </Tag>
          )}

          {/* Header */}
          <div className="flex justify-between md:items-center mb-5 md:mb-2">
            <div>
              <h1
                data-testid="article-title"
                className="text-[14px] font-semibold md:font-bold text-reliableBlack leading-[20px] lg:leading-[48px] lg:text-[28px] pr-4 lg:pr-8"
                style={{ overflowWrap: 'anywhere' }}
              >
                {article?.titlePage}
              </h1>
            </div>
            <div>
              {!enableSharing ? (
                <ShareButton
                  onClick={() => handleShareButtonClicked('goToShare', article?.titlePage)}
                  aria-label="share button"
                />
              ) : (
                <ShareButton aria-label="share button" />
              )}
            </div>
          </div>

          {/* Date */}
          <div className="mb-5 md:mb-7">
            <div className="flex items-center gap-2">
              <MdOutlineCalendarMonth size={18} />
              <p className="text-sm text-reliableBlack80 lg:text-base">
                <span className="inline-block mr-1">Diterbitkan</span>
                {dateFormatter(article.publishedDate, enableSharing)}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="mb-5 md:mb-7">
            {!enableSharing ? (
              <div id="detailContentArticle">
                {article?.detail?.detailContent ? (
                  <div
                    style={{ overflowWrap: 'anywhere' }}
                    className="prose max-sm:prose-li:text-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: article?.detail?.detailContent }}
                  />
                ) : null}
              </div>
            ) : (
              <div
                id="detailContentArticle"
                style={{ overflowWrap: 'anywhere' }}
                className="prose max-sm:prose-li:text-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: article?.detailContent }}
              ></div>
            )}
          </div>
        </div>

        {/* Share Modal */}
        {modalVisible && !enableSharing && (
          <ModalShare
            article={article}
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            actionClick={(buttonName) => {
              if (feature === 'NEWS_DETAIL') {
                eventClickNewsDetail(article, emarsysData?.breadcrumbs, buttonName, 'bagikan');
              } else if (feature === 'PRODUCT_KNOWLEDGE_DETAIL') {
                eventClickProductKnowledgeDetail(
                  article?.titlePage,
                  emarsysData?.breadcrumbs,
                  article?.publishedDate,
                  'bagikan',
                  buttonName
                );
              }
            }}
          />
        )}
      </section>
    </React.Fragment>
  );
};

export default DetailArticleBodySection;
